/**
 * 桌面端左侧图标导航竖栏（21n，人工指出「底部窗口 pc 时应该改成侧栏」）。
 *
 * 此前桌面端直接复用了移动端的**底部 Tab 栏**——那是「把移动端布局放大」，
 * 不是桌面形态。旧版桌面端是 `图标竖栏 | 列表栏 | 详情栏` 三列，PROMPT 的
 * 「PC：左侧会话列表 + 右侧聊天窗口并排」也是这个意思。
 *
 * 口径：
 * - **只在桌面渲染**（移动端仍是底部 Tab，由 HomePage 自己决定），所以本组件
 *   由 DesktopShell 挂在列表栏左侧，不进 HomePage——HomePage 两端共用。
 * - 未读角标复用 `badgeCountOf`（非免打扰会话未读之和，与系统角标/通知同口径），
 *   **零请求**：数据全来自已回流的会话行。
 * - 图标自己画描边 SVG（meIcons 同手法，不引图标库）。本组件在首屏 bundle 里，
 *   所以只画这三个、不建图标 barrel（待办发现里 21d 的分包口径）。
 *
 * 栏顶 logo、栏底头像与齿轮（21n-2 补；人工指出侧边栏缺这两处）：
 * - **头像零 HTTP**：不拉 `user/profile`，而是用 `auth` 切片里的昵称/头像快照——
 *   它来自凭证库的账号记录（`noteProfile` 回写），冷启动从 IndexedDB 读出来就有。
 *   21n 当时因为「以为必须拉 profile」跳过了这两处，其实凭证库早就存着。
 *   新账号还没拉过 profile 时快照是空的，回落成首字母渐变头像（avatarColor 同口径）。
 * - 头像点击进**编辑个人资料**、齿轮进**我的 Tab**（设置列表）——两个目标不同，
 *   不与「我的」导航项重复。
 */
import type { JSX } from 'preact';
import { useGlobal } from '~/store/useGlobal';
import { badgeCountOf } from '~/push/badge';
import { applyBadgeCount } from './applyEntryModel';
import { openMyProfile, switchHomeTab } from '~/nav/actions';
import { appRouter } from '~/nav/appRouter';
import { avatarStyle } from '~/chat/avatarColor';
import { resolveMediaUrl } from '~/chat/mediaUrl';
import { BRAND_NAME } from '~/brand';
import type { HomeTab } from '~/nav/routes';
import styles from './navRail.module.scss';

const ITEMS: ReadonlyArray<{ tab: HomeTab; label: string }> = [
  { tab: 'chats', label: '消息' },
  { tab: 'contacts', label: '通讯录' },
  { tab: 'me', label: '我的' },
];

const SVG: JSX.SVGAttributes<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 1.8,
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
};

function RailIcon({ tab }: { tab: HomeTab }) {
  if (tab === 'chats') {
    return (
      <svg {...SVG} class={styles.icon} aria-hidden="true">
        <path d="M20.4 12.4c0 4-3.8 7.2-8.4 7.2-1 0-2-.15-2.9-.42L4.2 20.6l1.5-4a6.9 6.9 0 0 1-2.1-4.8c0-4 3.8-7.2 8.4-7.2s8.4 3.2 8.4 7.2z" />
      </svg>
    );
  }
  if (tab === 'contacts') {
    return (
      <svg {...SVG} class={styles.icon} aria-hidden="true">
        <rect x="3.6" y="4.4" width="16.8" height="15.2" rx="2.4" />
        <circle cx="12" cy="10.6" r="2.4" />
        <path d="M8.2 16.4c0-2 1.7-3.1 3.8-3.1s3.8 1.1 3.8 3.1" />
      </svg>
    );
  }
  return (
    <svg {...SVG} class={styles.icon} aria-hidden="true">
      <circle cx="12" cy="8.4" r="3.6" />
      <path d="M4.6 19.8c0-3.4 3.2-5.4 7.4-5.4s7.4 2 7.4 5.4" />
    </svg>
  );
}

/**
 * 应用 logo（旧版栏顶同位）：品牌砖红圆角方块 + 米白云朵气泡，与 PWA 图标同形
 * （`scripts/gen-pwa-assets.mjs` 画的是同一个图案）。
 * 不引 `/icons/icon-192.png`：位图在高 DPI 下要么糊要么多下一份资源；
 * 这里是内联 SVG，零资源、任意尺寸清晰。
 * **用品牌色不用 accent**：accent 是界面主题色（用户能在外观设置里换），
 * logo 是品牌色，两者不该绑在一起。
 */
function RailLogo() {
  return (
    <span className={styles.logo} data-testid="rail-logo" aria-hidden="true" title={BRAND_NAME}>
      <svg viewBox="0 0 24 24" class={styles.logoGlyph}>
        {/* 云朵：三个圆 + 底部横条，同色自然并成一块 */}
        <g fill="var(--color-logo-fg)">
          <circle cx="12" cy="10.2" r="5.1" />
          <circle cx="7.6" cy="13" r="3.9" />
          <circle cx="16.4" cy="13.2" r="3.9" />
          <rect x="7.6" y="12" width="8.8" height="4.9" />
          {/* 三个点：挖在气泡上（画成底色） */}
          <g fill="var(--color-logo-bg)">
            <circle cx="9.2" cy="12.4" r="1.25" />
            <circle cx="12" cy="12.4" r="1.25" />
            <circle cx="14.8" cy="12.4" r="1.25" />
          </g>
          {/* 卷尾 */}
          <path
            d="M8.2 16.6c-2 0-3.4 1.3-3.4 3s1.3 2.9 2.9 2.9 2.7-1.1 2.7-2.5-1-2.2-2.1-2.2-1.8.7-1.8 1.6.6 1.4 1.3 1.4.9-.3 1-.7"
            fill="none"
            stroke="var(--color-logo-fg)"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </g>
      </svg>
    </span>
  );
}

/** 栏底头像：零 HTTP（auth 切片里的凭证库快照，见文件头） */
function RailAvatar() {
  const me = useGlobal((s) => (s.auth.status === 'logged-in' ? s.auth : null));
  const cdn = useGlobal((s) => s.config?.cdn ?? '');
  if (me === null) return null;
  const name = me.nickname;
  const url = resolveMediaUrl(cdn, me.avatar);
  return (
    <button
      type="button"
      className={styles.avatarBtn}
      data-testid="rail-avatar"
      title={name !== '' ? name : '我的资料'}
      aria-label="我的资料"
      onClick={() => openMyProfile(appRouter())}
    >
      <span className={styles.avatar} style={avatarStyle(name)}>
        <span className={styles.avatarChar}>{[...name][0] ?? '?'}</span>
        {url !== '' && (
          <img
            className={styles.avatarImg}
            src={url}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
      </span>
    </button>
  );
}

export function DesktopNavRail({ active }: { active: HomeTab }) {
  // 只订阅 rows 引用（appBadge 同策）：真正的 O(n) 求和只在 rows 换对象时跑
  const rows = useGlobal((s) => s.conversations.rows);
  const unread = badgeCountOf(rows);
  // 申请红点（21ag，人工报「通讯录这里还是没有红点提示」）：待处理好友/入群
  // 申请之和，与通讯录页入口角标、会话列表的申请会话同一份 contacts.points
  const applies = applyBadgeCount(useGlobal((s) => s.contacts.points));
  return (
    <nav className={styles.rail} data-testid="nav-rail">
      <RailLogo />
      {ITEMS.map(({ tab, label }) => (
        <button
          key={tab}
          type="button"
          // 与移动端底部 Tab **共用 testid**：两者互斥（桌面只有栏、移动端只有底栏），
          // e2e 里 34 处既有的 tab-* 点击因此两端通用，不必各写一套
          data-testid={`tab-${tab}`}
          data-rail="true"
          aria-current={tab === active ? 'page' : undefined}
          className={tab === active ? styles.itemActive : styles.item}
          onClick={() => switchHomeTab(appRouter(), tab)}
        >
          <span className={styles.iconWrap}>
            <RailIcon tab={tab} />
            {tab === 'chats' && unread > 0 && (
              <span className={styles.badge} data-testid="rail-unread">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
            {tab === 'contacts' && applies > 0 && (
              <span className={styles.badge} data-testid="rail-applies">
                {applies > 99 ? '99+' : applies}
              </span>
            )}
          </span>
          <span className={styles.label}>{label}</span>
        </button>
      ))}
      {/* 栏底：头像（进编辑资料）+ 齿轮（进我的 Tab 设置列表） */}
      <span className={styles.spacer} />
      <RailAvatar />
      <button
        type="button"
        className={styles.gear}
        data-testid="rail-settings"
        aria-label="设置"
        title="设置"
        onClick={() => switchHomeTab(appRouter(), 'me')}
      >
        <svg {...SVG} class={styles.icon}>
          <circle cx="12" cy="12" r="3.2" />
          <path d="M19.1 14.4a1.6 1.6 0 0 0 .3 1.8l.06.06a1.9 1.9 0 1 1-2.7 2.7l-.06-.06a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.17a1.9 1.9 0 1 1-3.8 0v-.09a1.6 1.6 0 0 0-1.05-1.5 1.6 1.6 0 0 0-1.8.3l-.06.06a1.9 1.9 0 1 1-2.7-2.7l.06-.06a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1h-.17a1.9 1.9 0 1 1 0-3.8h.09a1.6 1.6 0 0 0 1.5-1.05 1.6 1.6 0 0 0-.3-1.8l-.06-.06a1.9 1.9 0 1 1 2.7-2.7l.06.06a1.6 1.6 0 0 0 1.8.3h.08a1.6 1.6 0 0 0 1-1.5v-.17a1.9 1.9 0 1 1 3.8 0v.09a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.06-.06a1.9 1.9 0 1 1 2.7 2.7l-.06.06a1.6 1.6 0 0 0-.3 1.8v.08a1.6 1.6 0 0 0 1.5 1h.17a1.9 1.9 0 1 1 0 3.8h-.09a1.6 1.6 0 0 0-1.5 1z" />
        </svg>
      </button>
    </nav>
  );
}
